import { createFileRoute } from '@tanstack/react-router';
import { CheckIcon, Copy, RefreshCw, Settings } from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ProtectedRoute } from '@/components/auth/protected-route';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Label } from '@/components/ui/label';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Switch } from '@/components/ui/switch';
import {
  useCalendarSettings,
  useRegenerateCalendarToken,
  useUpdateCalendarSettings,
} from '@/hooks/use-calendar-settings';
import { useToast } from '@/hooks/use-toast';

export const Route = createFileRoute('/settings')({
  component: () => (
    <ProtectedRoute>
      <SettingsPage />
    </ProtectedRoute>
  ),
});

function SettingsPage() {
  const { t } = useTranslation();
  const { toast } = useToast();
  const [copiedUrl, setCopiedUrl] = useState(false);

  const { data: settingsData, isLoading } = useCalendarSettings();
  const updateSettings = useUpdateCalendarSettings();
  const regenerateToken = useRegenerateCalendarToken();

  const copyToClipboard = async (text: string) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopiedUrl(true);
      toast({
        title: t('settings.urlCopied'),
        description: t('settings.urlCopiedDescription'),
      });
      setTimeout(() => setCopiedUrl(false), 2000);
    } catch {
      toast({
        title: t('common.error'),
        description: t('settings.copyError'),
        variant: 'destructive',
      });
    }
  };

  const handleScopeChange = (scope: 'own' | 'all') => {
    updateSettings.mutate({ calendarScope: scope });
  };

  const handleBusyChange = (showAsBusy: boolean) => {
    updateSettings.mutate({ showAsBusy });
  };

  const handleRegenerateToken = () => {
    regenerateToken.mutate(undefined, {
      onSuccess: () => {
        toast({
          title: t('settings.tokenRegenerated'),
          description: t('settings.tokenRegeneratedDescription'),
        });
      },
      onError: () => {
        toast({
          title: t('common.error'),
          description: t('settings.regenerateError'),
          variant: 'destructive',
        });
      },
    });
  };

  if (isLoading) {
    return (
      <div className="space-y-6">
        <h1 className="text-3xl font-bold">{t('settings.title')}</h1>
        <div className="animate-pulse space-y-4">
          <div className="h-32 bg-gray-200 rounded-lg" />
          <div className="h-32 bg-gray-200 rounded-lg" />
        </div>
      </div>
    );
  }

  const settings = settingsData?.preferences;
  const calendarUrl = settingsData?.calendarUrl;

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-2">
        <Settings className="h-8 w-8" />
        <h1 className="text-3xl font-bold">{t('settings.title')}</h1>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>{t('settings.calendarSubscription')}</CardTitle>
          <CardDescription>{t('settings.calendarSubscriptionDescription')}</CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          <div className="space-y-2">
            <Label htmlFor="calendar-url">{t('settings.calendarUrl')}</Label>
            <div className="flex gap-2">
              <input
                id="calendar-url"
                value={calendarUrl || ''}
                readOnly
                className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
              />
              <Button
                variant="outline"
                size="icon"
                onClick={() => copyToClipboard(calendarUrl || '')}
                disabled={!calendarUrl}
              >
                {copiedUrl ? <CheckIcon className="h-4 w-4" /> : <Copy className="h-4 w-4" />}
              </Button>
            </div>
            <p className="text-sm text-muted-foreground">{t('settings.calendarUrlHelp')}</p>
          </div>

          <div className="flex justify-between items-center">
            <div>
              <h3 className="text-sm font-medium">{t('settings.regenerateUrl')}</h3>
              <p className="text-sm text-muted-foreground">{t('settings.regenerateUrlDescription')}</p>
            </div>
            <Button
              variant="outline"
              onClick={handleRegenerateToken}
              disabled={regenerateToken.isPending}
              className="flex items-center gap-2"
            >
              <RefreshCw className={`h-4 w-4 ${regenerateToken.isPending ? 'animate-spin' : ''}`} />
              {t('settings.regenerate')}
            </Button>
          </div>

          <hr className="border-border" />

          <div className="space-y-6">
            <div>
              <h3 className="text-sm font-medium mb-3">{t('settings.calendarPreferences')}</h3>
              <p className="text-sm text-muted-foreground mb-4">{t('settings.calendarPreferencesDescription')}</p>
            </div>

            <div className="space-y-3">
              <Label>{t('settings.appointmentScope')}</Label>
              <RadioGroup
                value={settings?.calendarScope || 'own'}
                onValueChange={(value: string) => handleScopeChange(value as 'own' | 'all')}
              >
                <div className="flex items-center space-x-2">
                  <RadioGroupItem value="own" id="scope-own" />
                  <Label htmlFor="scope-own">{t('settings.ownAppointments')}</Label>
                </div>
                <div className="flex items-center space-x-2">
                  <RadioGroupItem value="all" id="scope-all" />
                  <Label htmlFor="scope-all">{t('settings.allAppointments')}</Label>
                </div>
              </RadioGroup>
            </div>

            <div className="flex items-center justify-between">
              <div className="space-y-0.5">
                <Label htmlFor="show-busy">{t('settings.showAsBusy')}</Label>
                <p className="text-sm text-muted-foreground">{t('settings.showAsBusyDescription')}</p>
              </div>
              <Switch id="show-busy" checked={settings?.showAsBusy ?? true} onCheckedChange={handleBusyChange} />
            </div>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>{t('settings.instructions')}</CardTitle>
          <CardDescription>{t('settings.instructionsDescription')}</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div>
            <h4 className="font-medium mb-2">{t('settings.googleCalendar')}</h4>
            <ol className="list-decimal list-inside text-sm text-muted-foreground space-y-1">
              <li>{t('settings.googleStep1')}</li>
              <li>{t('settings.googleStep2')}</li>
              <li>{t('settings.googleStep3')}</li>
              <li>{t('settings.googleStep4')}</li>
            </ol>
          </div>

          <div>
            <h4 className="font-medium mb-2">{t('settings.outlookCalendar')}</h4>
            <ol className="list-decimal list-inside text-sm text-muted-foreground space-y-1">
              <li>{t('settings.outlookStep1')}</li>
              <li>{t('settings.outlookStep2')}</li>
              <li>{t('settings.outlookStep3')}</li>
              <li>{t('settings.outlookStep4')}</li>
            </ol>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
